import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveSunburst } from '@nivo/sunburst';

// Define recursive PropTypes for tree structure
const TreeNodeShape = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.number,
    children: PropTypes.array
};

TreeNodeShape.children = PropTypes.arrayOf(PropTypes.shape(TreeNodeShape));

// Extract tooltip component
const CustomTooltip = ({ data, id, value, color }) => (
    <div style={{
        background: '#1a1a1a',
        padding: '12px',
        border: '1px solid #444444',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        color: '#ffffff',
        fontSize: '14px'
    }}>
        <strong>{data.name}</strong>
        <br />
        {(() => {
            if (value) return `Count: ${value}`;
            if (data.children) return 'Click to drill down';
            return '';
        })()}
    </div>
);

CustomTooltip.propTypes = {
    data: PropTypes.shape(TreeNodeShape),
    id: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string
};

// Extract center label component
const CenterLabel = ({ centerX, centerY, fullPathNames, currentTotal }) => (
    <g>
        {fullPathNames.length > 0 ? (
            <>
                {fullPathNames.map((name, i) => (
                    <text
                        key={`path-${name}-${i}`} // Using name+index for uniqueness
                        x={centerX}
                        y={centerY - ((fullPathNames.length * 15) / 2) + (i * 15)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                            fill: '#ffffff',
                            fontSize: '12px',
                            fontWeight: i === fullPathNames.length - 1 ? 'bold' : 'normal'
                        }}
                    >
                        {name}
                    </text>
                ))}
                <text
                    x={centerX}
                    y={centerY + (fullPathNames.length * 15 / 2) + 10}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ fill: '#ffffff', fontSize: '16px', fontWeight: 'bold' }}
                >
                    {currentTotal}
                </text>
            </>
        ) : (
            <>
                <text
                    x={centerX}
                    y={centerY - 8}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ fill: '#ffffff', fontSize: '12px', fontWeight: 'bold' }}
                >
                    Total Locks
                </text>
                <text
                    x={centerX}
                    y={centerY + 8}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ fill: '#ffffff', fontSize: '16px', fontWeight: 'bold' }}
                >
                    {currentTotal}
                </text>
            </>
        )}
    </g>
);

CenterLabel.propTypes = {
    centerX: PropTypes.number.isRequired,
    centerY: PropTypes.number.isRequired,
    fullPathNames: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentTotal: PropTypes.number.isRequired
};

const ChartLayer = (props) => {
    const { centerX, centerY } = props;
    const { fullPathNames, currentTotal } = props.data;
    const lineHeight = 15;
    const textStartY = centerY - (fullPathNames?.length * lineHeight / 2);

    return (
        <g>
            {fullPathNames?.length > 0 ? (
                <>
                    {fullPathNames.map((name, index) => (
                        <text
                            key={`${name}-${index}`}
                            x={centerX}
                            y={textStartY + (index * lineHeight)}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{
                                fill: '#ffffff',
                                fontSize: '12px',
                                fontWeight: index === fullPathNames.length - 1 ? 'bold' : 'normal'
                            }}
                        >
                            {name}
                        </text>
                    ))}
                    <text
                        x={centerX}
                        y={textStartY + (fullPathNames.length * lineHeight) + 10}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fill: '#ffffff', fontSize: '16px', fontWeight: 'bold' }}
                    >
                        {currentTotal}
                    </text>
                </>
            ) : (
                <>
                    <text
                        x={centerX}
                        y={centerY - 8}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fill: '#ffffff', fontSize: '12px', fontWeight: 'bold' }}
                    >
                        Total Locks
                    </text>
                    <text
                        x={centerX}
                        y={centerY + 8}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fill: '#ffffff', fontSize: '16px', fontWeight: 'bold' }}
                    >
                        {currentTotal}
                    </text>
                </>
            )}
        </g>
    );
};

ChartLayer.propTypes = {
    centerX: PropTypes.number.isRequired,
    centerY: PropTypes.number.isRequired,
    data: PropTypes.shape({
        fullPathNames: PropTypes.arrayOf(PropTypes.string),
        currentTotal: PropTypes.number
    }).isRequired
};

// Outside PickLocationAnalysis component
const renderChartLayer = (props, data) => <ChartLayer {...props} data={data} />;

const PickLocationAnalysis = ({ data }) => {
    const [currentPath, setCurrentPath] = useState([]);

    if (!data || !data.children) {
        return <div className="chart-container pick-location-analysis">No data available</div>;
    }

    // Function to get drilled down data based on current path
    const getDrilledDownData = (fullData, path) => {
        if (path.length === 0) return fullData;

        let current = { ...fullData };

        // For debugging
        console.log('Starting path traversal with:', path);
        console.log('Initial data:', current);

        for (const id of path) {
            // Search through all children recursively to find the matching id
            const findNode = (node) => {
                if (node.id === id) return node;
                if (!node.children) return null;

                for (const child of node.children) {
                    const found = findNode(child);
                    if (found) return found;
                }
                return null;
            };

            const found = findNode(current);
            if (!found) {
                console.log('Could not find node with id:', id);
                return current;
            }

            console.log('Found node:', found);
            current = found;
        }

        return current;
    };

    // Calculate total from current node
    const calculateTotal = (node) => {
        if (node.value) return node.value;
        if (!node.children) return 0;
        return node.children.reduce((sum, child) => sum + calculateTotal(child), 0);
    };

    const currentData = getDrilledDownData(data, currentPath);
    const currentTotal = calculateTotal(currentData);

    // Get the complete path names for display
    const getFullPathNames = () => {
        if (currentPath.length === 0) return [];

        let fullPath = [];

        // Function to find a node by ID and collect its ancestors
        const findNodeAndPath = (node, targetId) => {
            if (node.id === targetId) {
                return [node];
            }

            if (!node.children) {
                return null;
            }

            for (const child of node.children) {
                const path = findNodeAndPath(child, targetId);
                if (path) {
                    return [node, ...path];
                }
            }

            return null;
        };

        // Get the full path for the last ID in currentPath
        const lastId = currentPath[currentPath.length - 1];
        const nodePath = findNodeAndPath(data, lastId);

        if (nodePath) {
            // Skip the root node if it exists
            fullPath = nodePath.slice(1).map(node => node.name);
        }

        return fullPath;
    };

    const fullPathNames = getFullPathNames();

    const handleClick = (node) => {
        console.log('Clicked node:', node);
        console.log('Node data:', node.data);

        // Don't drill down if we're at a leaf node (no children)
        if (!node.data.children) {
            console.log('Leaf node clicked, no drilling');
            return;
        }

        // Find where this node is in the current hierarchy
        const nodeId = node.id;
        console.log('Clicked node ID:', nodeId);

        setCurrentPath(prev => {
            // If we're clicking a node that's already in our path,
            // trim the path up to that point
            const existingIndex = prev.indexOf(nodeId);
            if (existingIndex !== -1) {
                console.log('Node already in path, trimming to:', existingIndex);
                return prev.slice(0, existingIndex + 1);
            }

            // Otherwise add this node to our path
            const newPath = [...prev, nodeId];
            console.log('New path:', newPath);
            return newPath;
        });
    };

    return (
        <div className="chart-container pick-location-analysis">
            <h3>
                Pick Location Analysis
                {currentPath.length > 0 && (
                    <button
                        onClick={() => setCurrentPath(prev => prev.slice(0, -1))}
                        style={{
                            marginLeft: '10px',
                            padding: '5px 10px',
                            borderRadius: '4px',
                            border: 'none',
                            backgroundColor: '#444',
                            color: 'white',
                            cursor: 'pointer'
                        }}
                    >
                        ← Back
                    </button>
                )}
            </h3>
            <div style={{ height: 450 }}>
                <ResponsiveSunburst
                    data={currentData}
                    margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
                    id="id"
                    value="value"
                    cornerRadius={3}
                    borderWidth={3}
                    borderColor="#1a1a1a"
                    colors={({ id, depth, data }) => {
                        // For debugging the incoming data
                        console.log('Color assignment data:', { id, depth, data });

                        // Get the last segment of the ID to determine the type
                        const segments = id.split('-');
                        // Center
                        if (depth === 0) return '#2d2d2d';

                        // First ring (Favorite/Underdog/Pick'em/Over-Under)
                        if (data.name === 'Favorite') return '#82ca9d';
                        if (data.name === 'Underdog') return '#8884d8';
                        if (data.name === 'Pick\'em' || data.name === "Pick'em") return '#ffd700';  // Handle both quote types
                        if (data.name === 'Over/Under') return '#ff9800';

                        // Second ring (Home/Away/Over/Under)
                        if (data.name === 'Home') return '#ffc658';
                        if (data.name === 'Away') return '#ff8042';
                        if (data.name === 'Over') return '#9c27b0';
                        if (data.name === 'Under') return '#673ab7';

                        // Third ring (Wins/Losses)
                        if (data.name === 'Wins') return '#4caf50';
                        if (data.name === 'Losses') return '#f44336';

                        // Fourth ring (Margins)
                        if (data.value) {  // Leaf nodes (margins)
                            const parentName = segments[segments.length - 2];
                            if (parentName === 'won') return '#81c784';
                            if (parentName === 'lost') return '#e57373';
                        }

                        return '#888';
                    }}
                    enableArcLabels={true}
                    arcLabelsSkipAngle={12}
                    arcLabel={d => {
                        // Add debugging for label data
                        console.log('Arc label data:', d);

                        if (!d || !d.data) return '';
                        if (d.data.value) {
                            return `${d.data.name} (${d.data.value})`;
                        }
                        return d.data.name;
                    }}
                    arcLabelsTextColor={{
                        from: 'color',
                        modifiers: [
                            ['darker', 3]
                        ]
                    }}
                    transitionMode="pushOut"
                    animate={true}
                    motionConfig="wobbly"
                    arcHoverOffset={10}
                    arcHoverOpacity={1}
                    arcHoverTransitionDuration={250}
                    interactivity={{
                        enable: true,
                        draggable: false,
                        zoomable: false
                    }}
                    defs={[
                        {
                            id: 'hoverGradient',
                            type: 'linearGradient',
                            colors: [
                                { offset: 0, color: 'inherit' },
                                { offset: 100, color: 'inherit', opacity: 0.8 }
                            ]
                        }
                    ]}
                    fill={[
                        { match: { depth: 0 }, id: 'hoverGradient' }
                    ]}
                    onClick={handleClick}
                    tooltip={CustomTooltip}
                    layers={[
                        'arcs',
                        'arcLabels',
                        'arcLinkLabels',
                        props => renderChartLayer(props, { fullPathNames, currentTotal })
                    ]}
                />
            </div>
        </div>
    );
};

PickLocationAnalysis.propTypes = {
    data: PropTypes.shape(TreeNodeShape)
};

export default PickLocationAnalysis;
